// src/hooks/useMessageTranslation.ts
import { useState, useEffect, useRef } from "react";

// ─── Simple cache ──────────────────────────────────────────────────────────
const cache = new Map<string, string>();

async function googleTranslate(
  text: string,
  targetLang: string,
): Promise<string> {
  if (!text?.trim()) return text;

  const key = `${targetLang}::${text}`;
  if (cache.has(key)) return cache.get(key)!;

  try {
    const url =
      `https://translate.googleapis.com/translate_a/single` +
      `?client=gtx&sl=auto&tl=${encodeURIComponent(targetLang)}&dt=t&q=${encodeURIComponent(text)}`;
    const res = await fetch(url);
    if (!res.ok) return text;
    const data = await res.json();
    const translated =
      (data?.[0] as any[])?.map((chunk: any) => chunk?.[0] ?? "").join("") ??
      "";
    const result = translated || text;
    cache.set(key, result);
    return result;
  } catch {
    return text;
  }
}

function getActiveLang(): string {
  return localStorage.getItem("regalflux_language") || "en";
}

/**
 * Translates a message into the active UI language.
 * Uses sl=auto so Google detects the source language automatically.
 * When UI is English and message is already English, Google returns it unchanged
 * so isTranslated stays false and no toggle is shown.
 */
export function useMessageTranslation(message: string) {
  const [display, setDisplay] = useState(message);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isTranslated, setIsTranslated] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const msgRef = useRef(message);
  msgRef.current = message;

  const run = async (text: string, lang: string) => {
    if (!text?.trim()) {
      setDisplay(text);
      setIsTranslated(false);
      return;
    }
    setIsTranslating(true);
    try {
      const result = await googleTranslate(text, lang);
      setDisplay(result);
      setIsTranslated(result !== text);
    } catch {
      setDisplay(text);
      setIsTranslated(false);
    } finally {
      setIsTranslating(false);
    }
  };

  useEffect(() => {
    run(message, getActiveLang());
    setShowOriginal(false);
  }, [message]);

  useEffect(() => {
    const handler = () => {
      run(msgRef.current, getActiveLang());
      setShowOriginal(false);
    };
    window.addEventListener("regalflux_lang_change", handler);
    return () => window.removeEventListener("regalflux_lang_change", handler);
  }, []);

  return {
    display,
    isTranslating,
    isTranslated,
    showOriginal,
    toggleOriginal: () => setShowOriginal((v) => !v),
  };
}
